/**
 * Pure parsing of {{variable}} template placeholders, mirroring the backend
 * (server/common_utils/template_variable_ast.py + template_variable_parsing.py). A placeholder is
 * `{{ head [ or operand ]* }}` where `head` is a system variable, a dotted job attribute, or a
 * Lookup Variable `label.column`, followed by zero or more `or`-separated fallback operands. Each
 * operand is either another variable reference or a quoted string literal; the first quoted
 * literal in the chain is the effective default (it short-circuits the chain) and renders when the
 * earlier variables have no value. Quotes are required for a default so there is one canonical form
 * (`{{label.column or ""}}`): a bare `or 20` is NOT a usable fallback and is flagged at save.
 *
 * Parsing is a small recursive-descent scan (the `Scanner` class) that mirrors the backend
 * `_Scanner` exactly: it stays tolerant (always yields an AST so coloring never breaks) while
 * accumulating human-readable SYNTAX errors (a dangling `or`, a malformed string literal) for the
 * editor to surface. Shared by variable coloring, the `{{` autocomplete, the save-time
 * may-render-blank warning, and syntax-error highlighting so the syntax stays consistent with
 * the server.
 */

import {
  NAMESPACE_PREFIXES,
  type NamespacePrefix,
} from 'utils/variableMetadata.generated';
export {
  NAMESPACE_PREFIXES,
  type NamespacePrefix,
} from 'utils/variableMetadata.generated';
import { isOneOf } from 'utils/typeGuards';

/** Inner content of a placeholder; matches the coloring regex /\{\{([^{}]+)\}\}/. */
const TEMPLATE_PLACEHOLDER_REGEX = /\{\{([^{}]+?)\}\}/g;

// The head is a lone `\S+` token immediately followed by ` or ` (`HEAD_REGEX` matches the token AND
// the first separator); `OR_REGEX` is each subsequent separator. Both are sticky (`y`) so a match
// is anchored at the scanner cursor. The negative lookahead after `or` is the word-boundary check:
// `or` is a separator only when NOT glued to another word character, so `or"$18"`/`or "$18"` split
// while `or20` stays part of the name. It is spelled with Unicode property classes (not `\b`, which
// is ASCII-only in JS) so the boundary matches Python's Unicode-aware `\b` — otherwise `{{a or bé}}`
// would parse as `[a, é]` here but `[a, "bé"]` on the backend. `[\p{L}\p{M}\p{N}\p{Pc}]` mirrors
// Python's `\w`. The trailing `\s*` swallows the next operand's leading space.
const WORD_CHAR = String.raw`\p{L}\p{M}\p{N}\p{Pc}`;
const HEAD_REGEX = new RegExp(
  String.raw`(\S+)\s+or(?![${WORD_CHAR}])\s*`,
  'uy'
);
const OR_REGEX = new RegExp(String.raw`\s+or(?![${WORD_CHAR}])\s*`, 'uy');

/**
 * A single SYNTAX error found while scanning a placeholder. It carries no display text: the UI
 * words it by `code` (`components/template/templateSyntaxErrorText.ts`).
 */
export type TemplateSyntaxIssue =
  | { code: 'dangling_or' }
  | {
      code: 'malformed_string_literal';
      /** The offending operand text. */
      segment: string;
    };

/** A single `or`-operand: a variable reference or a quoted string literal. */
export type TemplateOperand =
  | { kind: 'var'; name: string }
  | { kind: 'literal'; value: string };

export interface ParsedTemplateVariable {
  /** Head variable name only (e.g. `state-lookup.base_pay_rate`). */
  name: string;
  /**
   * Value of the FIRST quoted-literal operand in the `or` chain (the effective default), or
   * null when there is no `or` operand OR no operand is a quoted literal (an unquoted `or 20`
   * is treated as no usable fallback and flagged).
   */
  fallback: string | null;
  /** All operands in order; `alternatives[0]` is always the head (kind: 'var'). */
  alternatives: TemplateOperand[];
}

/** A parsed placeholder plus any hard SYNTAX errors (empty when well-formed). */
export interface ParsedTemplateVariableChecked extends ParsedTemplateVariable {
  errors: TemplateSyntaxIssue[];
}

/**
 * A syntax problem in a `{{...}}` placeholder, with its span in the source text. Syntax errors are
 * malformed constructs (a dangling `or`, an unterminated string literal) — distinct from the
 * warning-level type advisories (`analyzePlaceholderType`).
 */
export type TemplateSyntaxError = TemplateSyntaxIssue & {
  /** Inner text of the offending placeholder (without the braces). */
  placeholder: string;
  /** Offset of `{{` and just past `}}` in the source text (for editor highlighting). */
  start: number;
  end: number;
};

// Content of a quoted literal ("..."/'...'), or null when `text` is not a clean literal.
// Escape-aware (mirrors the backend `_literal_value` and the Scanner's `skipQuoted`): `\` escapes
// the next char, so an escaped terminal quote does NOT close the string (`"foo\"` is unterminated,
// not the literal `foo\`), and the closing quote must be the LAST char (no trailing text). Only
// `\<quote>` is unescaped in the value; other backslashes are kept literally.
const quotedFallbackValue = (text: string): string | null => {
  if (text.length < 2 || (text[0] !== '"' && text[0] !== "'")) {
    return null;
  }
  const quote = text[0];
  let i = 1;
  while (i < text.length) {
    const ch = text[i];
    if (ch === '\\' && i + 1 < text.length) {
      i += 2; // escaped character (any) — does not close the string
      continue;
    }
    if (ch === quote) {
      return i === text.length - 1
        ? text.slice(1, i).split(`\\${quote}`).join(quote)
        : null;
    }
    i += 1;
  }
  return null; // no unescaped closing quote -> unterminated
};

/**
 * Recursive-descent cursor over a placeholder's inner text (mirrors the backend `_Scanner`).
 * Quote-awareness lives in one place (`skipQuoted`), so an `or` inside a quoted literal is never
 * mistaken for a separator. Each parse step advances the cursor and records any syntax errors.
 */
class Scanner {
  private pos = 0;
  readonly errors: TemplateSyntaxIssue[] = [];

  constructor(private readonly text: string) {}

  /** Match `pattern` (sticky) at the cursor; on success advance past it and return the match. */
  consume(pattern: RegExp): RegExpExecArray | null {
    pattern.lastIndex = this.pos;
    const match = pattern.exec(this.text);
    if (match !== null) {
      this.pos = pattern.lastIndex;
    }
    return match;
  }

  /** Consume the next `or` separator, returning true when another operand follows. */
  moreOperands(): boolean {
    return this.consume(OR_REGEX) !== null;
  }

  /** Parse one `or` operand: a quoted literal or a variable reference (records errors). */
  parseOperand(): TemplateOperand {
    const segment = this.nextSegment();
    if (!segment) {
      this.errors.push({ code: 'dangling_or' });
      return { kind: 'var', name: '' };
    }
    if (segment[0] === '"' || segment[0] === "'") {
      const literal = quotedFallbackValue(segment);
      if (literal !== null) {
        return { kind: 'literal', value: literal };
      }
      // Opens a quote but is not a well-formed quoted string (unterminated, or trailing text after
      // the close). Keep it as a variable ref so coloring stays tolerant; report the error.
      this.errors.push({ code: 'malformed_string_literal', segment });
    }
    return { kind: 'var', name: segment };
  }

  /** One operand's text: up to the next top-level `or` (or end), skipping quoted spans. */
  private nextSegment(): string {
    const start = this.pos;
    while (this.pos < this.text.length) {
      const char = this.text.charAt(this.pos);
      if (char === '"' || char === "'") {
        this.skipQuoted();
      } else if (/\s/.test(char) && this.matchesOrAt(this.pos)) {
        break;
      } else {
        this.pos += 1;
      }
    }
    return this.text.slice(start, this.pos).trim();
  }

  /** True when the `OR_REGEX` separator matches at `pos` (without consuming). */
  private matchesOrAt(pos: number): boolean {
    OR_REGEX.lastIndex = pos;
    return OR_REGEX.test(this.text);
  }

  /** Advance past a quoted string, honoring `\` escapes (stops at end if unterminated). */
  private skipQuoted(): void {
    const quote = this.text[this.pos];
    this.pos += 1;
    while (this.pos < this.text.length) {
      const char = this.text[this.pos];
      if (char === '\\' && this.pos + 1 < this.text.length) {
        this.pos += 2;
        continue;
      }
      this.pos += 1;
      if (char === quote) {
        return;
      }
    }
  }
}

/** An href serializes quotes as entities. Decode them before validation and eval. */
const decodePlaceholderEntities = (inner: string): string =>
  inner
    .replace(/&quot;/g, '"')
    .replace(/&#x27;/gi, "'")
    .replace(/&#39;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&');

/**
 * Parse the inner text of a `{{...}}` placeholder into its head + `or`-separated operands, plus any
 * syntax errors. `HEAD_REGEX` matches the head token AND its first ` or ` separator; no match means
 * there is no valid `or` clause, so the whole inner is a single variable name.
 */
export const parseTemplateVariableChecked = (
  inner: string
): ParsedTemplateVariableChecked => {
  const trimmed = decodePlaceholderEntities(inner).trim();
  const scanner = new Scanner(trimmed);
  // Group 1 always takes part in a match.
  const headName = scanner.consume(HEAD_REGEX)?.[1];
  if (headName === undefined) {
    return {
      name: trimmed,
      fallback: null,
      alternatives: [{ kind: 'var', name: trimmed }],
      errors: [],
    };
  }
  const fallbacks: TemplateOperand[] = [scanner.parseOperand()];
  while (scanner.moreOperands()) {
    fallbacks.push(scanner.parseOperand());
  }
  const firstLiteral = fallbacks.find(
    (op): op is Extract<TemplateOperand, { kind: 'literal' }> =>
      op.kind === 'literal'
  );
  return {
    name: headName,
    // The first quoted literal short-circuits the chain, so it is the effective default.
    fallback: firstLiteral ? firstLiteral.value : null,
    alternatives: [{ kind: 'var', name: headName }, ...fallbacks],
    errors: scanner.errors,
  };
};

/** Parse the inner text of a `{{...}}` placeholder into its head + `or`-separated operands. */
export const parseTemplateVariable = (
  inner: string
): ParsedTemplateVariable => {
  const { name, fallback, alternatives } = parseTemplateVariableChecked(inner);
  return { name, fallback, alternatives };
};

/**
 * The LIVE variable names a placeholder references — the head plus each variable `or` operand up to
 * (but not past) the first quoted literal, mirroring the backend `variable_names`. A literal
 * short-circuits the chain (first literal renders and stops), so any operand after it is dead and
 * never evaluates; only these live operands are actually resolved/validated.
 */
export const liveVariableNamesFromOperands = (
  alternatives: readonly TemplateOperand[]
): string[] => {
  const names: string[] = [];
  for (const operand of alternatives) {
    if (operand.kind === 'literal') {
      break;
    }
    names.push(operand.name);
  }
  return names;
};

export const liveVariableNames = (inner: string): string[] =>
  liveVariableNamesFromOperands(parseTemplateVariable(inner).alternatives);

/** Static TYPE analysis of one placeholder chain (mirrors backend `analyze_placeholder_type`). */
export interface PlaceholderTypeAnalysis {
  /** The chain's type is `Some[str]` — it may resolve to nothing (render blank). */
  isOptional: boolean;
  /** Display text of operands after the chain is decided — they can never evaluate. */
  unreachableOperands: string[];
}

const operandDisplay = (operand: TemplateOperand): string =>
  operand.kind === 'literal' ? `"${operand.value}"` : operand.name;

/**
 * The static type analog of runtime evaluation — one left-to-right fold over TYPES, mirroring the
 * backend `analyze_placeholder_type`. `partialNames` are the `Some[str]` variables: lookup
 * `label.column` and derived names, which often do not resolve for a given job. Every other
 * variable is treated as ordinarily-present.
 *
 * The two diagnostics need different levels of certainty, so they use different evidence:
 * - `isOptional` ("may render blank") is advisory, so a non-partial operand suppresses it. This
 *   keeps ordinary `{{candidate_name}}` / `{{job_name}}` placeholders from being flagged on every
 *   save — an advisory that fires on everything is noise, not signal.
 * - `unreachableOperands` claims an operand can NEVER run, i.e. advice to delete it. Only a
 *   LITERAL justifies that, since it provably renders itself. A variable never does: built-ins
 *   are populated conditionally, so `{{company_name or "our team"}}` really can use its fallback.
 */
export const analyzeOperandsType = (
  alternatives: readonly TemplateOperand[],
  partialNames: ReadonlySet<string>
): PlaceholderTypeAnalysis => {
  // Unreachable: ONLY a literal proves later operands dead (it always renders itself). Computed
  // independently of the isOptional walk below, which may stop earlier on an ordinarily-present
  // variable — `{{job_name or "x" or "y"}}` must still report `"y"` as dead.
  const literalIndex = alternatives.findIndex(
    (operand) => operand.kind === 'literal'
  );
  const unreachableOperands =
    literalIndex >= 0
      ? alternatives.slice(literalIndex + 1).map(operandDisplay)
      : [];

  // May-render-blank: walk until something yields a value. A non-partial variable (built-in / job
  // attribute) is ordinarily present, so it suppresses the advisory; a blank literal renders empty.
  let isOptional = true;
  for (const operand of alternatives) {
    if (operand.kind === 'literal') {
      isOptional = operand.value.trim() === '';
      break;
    }
    if (!partialNames.has(operand.name)) {
      isOptional = false;
      break;
    }
  }
  return { isOptional, unreachableOperands };
};

export const analyzePlaceholderType = (
  inner: string,
  partialNames: ReadonlySet<string>
): PlaceholderTypeAnalysis =>
  analyzeOperandsType(parseTemplateVariable(inner).alternatives, partialNames);

/** True when the placeholder's TYPE is `Some[str]` (see `analyzePlaceholderType`). */
export const placeholderIsOptional = (
  inner: string,
  partialNames: ReadonlySet<string>
): boolean => analyzePlaceholderType(inner, partialNames).isOptional;

/**
 * The distinct `Some[str]`-typed placeholders in `text` (first-seen, as raw `{{...}}` display
 * text). These may render blank at substitution, so save paths surface them as a non-blocking
 * warning — never a save block.
 */
export const findOptionalPlaceholders = (
  text: string,
  partialNames: ReadonlySet<string>
): string[] => {
  const seen = new Set<string>();
  const optional: string[] = [];
  for (const match of text.matchAll(TEMPLATE_PLACEHOLDER_REGEX)) {
    const inner = match[1];
    if (
      inner !== undefined &&
      placeholderIsOptional(inner, partialNames) &&
      !seen.has(match[0])
    ) {
      seen.add(match[0]);
      optional.push(match[0]);
    }
  }
  return optional;
};

/** Every `{{...}}` syntax error in `text` (malformed operands), in order of appearance. */
export const findTemplateSyntaxErrors = (
  text: string
): TemplateSyntaxError[] => {
  const errors: TemplateSyntaxError[] = [];
  for (const match of text.matchAll(TEMPLATE_PLACEHOLDER_REGEX)) {
    const inner = match[1];
    if (inner === undefined) {
      continue;
    }
    const start = match.index ?? 0;
    const parsed = parseTemplateVariableChecked(inner);
    for (const issue of parsed.errors) {
      errors.push({
        ...issue,
        // Trim to match the backend's placeholder echo (its regex strips surrounding whitespace).
        placeholder: inner.trim(),
        start,
        end: start + match[0].length,
      });
    }
  }
  return errors;
};

/** True when `name` is a recognized Organization Variable. */
export const isOrganizationVariable = (
  name: string,
  organizationVariableNames: ReadonlySet<string>
): boolean => organizationVariableNames.has(name);

/**
 * A recognized namespace and its opaque tail. Namespace vocabulary is generated from the
 * backend registry; the parser above still treats dotted names as ordinary variable operands.
 */
export interface NamespaceSplit {
  prefix: NamespacePrefix;
  tail: string;
}

/**
 * Split `<prefix>.<tail>` for a RECOGNIZED namespace, else null. Byte-for-byte the semantics of the
 * backend `split_namespace`:
 *
 * - ONE level only (split at the FIRST `.`), so `job.custom_field_values.field_123` yields
 *   `{ prefix: 'job', tail: 'custom_field_values.field_123' }` with the tail intact.
 * - An unrecognized prefix returns null — a Lookup Variable (`state-lookup.rate`) or a bare ATS
 *   attribute (`location.state`) stays classified exactly as it was before namespacing.
 * - An empty tail (`call.`) is NOT a namespaced reference.
 */
export const splitNamespace = (name: string): NamespaceSplit | null => {
  const separatorIndex = name.indexOf('.');
  if (separatorIndex < 0) {
    return null;
  }
  const prefix = name.slice(0, separatorIndex);
  const tail = name.slice(separatorIndex + 1);
  if (!tail || !isOneOf(NAMESPACE_PREFIXES, prefix)) {
    return null;
  }
  return { prefix, tail };
};
